import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon, CheckCircleIcon } from '../icons/Icons';
import aiService from '@/services/aiService';

export interface GallerySelectedImage {
  id: string;
  backendId?: string | number;
  src: string;
}

// Minimal shape the picker needs from each gallery image.
interface PickerImage {
  id: string;
  backendId?: number;
  src: string;
  prompt?: string;
}

const PER_PAGE = 30;

interface GallerySelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (url: string, image?: GallerySelectedImage) => void;
  onSelectImages?: (images: GallerySelectedImage[]) => void;
  multiple?: boolean;
}

const GallerySelectorModal: React.FC<GallerySelectorModalProps> = ({
  isOpen,
  onClose,
  onSelectImage,
  onSelectImages,
  multiple = false,
}) => {
  const { t } = useTranslation('models');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Local pagination (infinite scroll) — independent of the shared gallery store so
  // the picker can page through the FULL gallery, not just the store's first 30.
  const [imageItems, setImageItems] = useState<PickerImage[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadPage = useCallback(async (pageNum: number, append: boolean) => {
    setLoading(true);
    try {
      const raw = await aiService.getUserImages({ per_page: PER_PAGE, page: pageNum });
      const mapped: PickerImage[] = (raw || []).map((img: any) => ({
        id: `img_${img.id}`,
        backendId: img.id,
        src: img.url,
        prompt: img.metadata?.prompt || img.prompt || undefined,
      }));
      // A short page (fewer than PER_PAGE) means we reached the end.
      setHasMore(mapped.length === PER_PAGE);
      setImageItems((prev) => (append ? [...prev, ...mapped] : mapped));
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch gallery page:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Fresh load each open so newly generated images show up.
      setImageItems([]);
      setHasMore(true);
      loadPage(1, false);
    } else {
      setSelectedId(null);
      setSelectedIds(new Set());
    }
  }, [isOpen, loadPage]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      loadPage(page + 1, true);
    }
  }, [loading, hasMore, page, loadPage]);

  if (!isOpen) return null;

  const toGalleryImage = (item: PickerImage): GallerySelectedImage => ({
    id: item.id,
    backendId: item.backendId,
    src: item.src,
  });

  const handleSelect = (item: typeof imageItems[0]) => {
    if (multiple) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(item.id)) {
          next.delete(item.id);
        } else {
          next.add(item.id);
        }
        return next;
      });
    } else {
      setSelectedId(item.id);
    }
  };

  const handleConfirm = () => {
    if (multiple) {
      const selected = imageItems.filter((item) => selectedIds.has(item.id));
      if (onSelectImages) {
        onSelectImages(selected.map(toGalleryImage));
      } else {
        selected.forEach((item) => onSelectImage(item.src, toGalleryImage(item)));
      }
      onClose();
    } else {
      const selected = imageItems.find((item) => item.id === selectedId);
      if (selected) {
        onSelectImage(selected.src, toGalleryImage(selected));
        onClose();
      }
    }
  };

  const isSelected = (id: string) => multiple ? selectedIds.has(id) : selectedId === id;
  const selectionCount = multiple ? selectedIds.size : (selectedId ? 1 : 0);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-snapfy-surface border border-white/10 w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-extrabold text-snapfy-text-primary">
            {t('uploadChoice.fromGallery')}
            {multiple && selectedIds.size > 0 && (
              <span className="ml-2 text-sm font-normal text-[var(--snapfy-accent)]">
                ({selectedIds.size} selecionado{selectedIds.size !== 1 ? 's' : ''})
              </span>
            )}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 text-snapfy-text-secondary hover:text-snapfy-text-primary hover:bg-white/10 rounded-xl transition-colors"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Gallery Grid — infinite scroll: pages in 30 at a time as you reach the bottom. */}
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto p-5">
          {imageItems.length === 0 ? (
            <div className="text-center py-16 text-snapfy-text-secondary">
              {loading ? (
                <span className="inline-block w-6 h-6 border-2 border-[var(--snapfy-accent)] border-t-transparent rounded-full animate-spin" />
              ) : (
                <p className="text-sm">{t('uploadChoice.emptyGallery')}</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {imageItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className={`aspect-square rounded-xl overflow-hidden border-2 cursor-pointer transition-all relative ${
                    isSelected(item.id)
                      ? 'border-[var(--snapfy-accent)] ring-2 ring-[var(--snapfy-accent)]/20 scale-[1.02]'
                      : 'border-white/10 hover:border-[var(--snapfy-accent)]/50'
                  }`}
                >
                  <img
                    src={item.src}
                    className="w-full h-full object-cover"
                    alt={item.prompt || 'Gallery image'}
                    loading="lazy"
                  />
                  {isSelected(item.id) && (
                    <div className="absolute inset-0 bg-[var(--snapfy-accent)]/20 flex items-center justify-center">
                      <div className="bg-[var(--snapfy-accent)] text-white rounded-full p-1.5 shadow-lg">
                        <CheckCircleIcon className="w-5 h-5" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {loading && imageItems.length > 0 && (
            <div className="flex justify-center py-4">
              <span className="inline-block w-5 h-5 border-2 border-[var(--snapfy-accent)] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Footer — always show confirm button */}
        {imageItems.length > 0 && (
          <div className="p-5 border-t border-white/10 flex-shrink-0">
            <button
              onClick={handleConfirm}
              disabled={selectionCount === 0}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#7063f7] to-[#5B4ED4] text-white rounded-xl text-sm font-bold shadow-lg shadow-[#7063f7]/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {multiple && selectedIds.size > 0
                ? t('uploadChoice.confirmImages', { count: selectedIds.size })
                : t('uploadChoice.selectImage')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GallerySelectorModal;
